import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { SwalAlert } from '@/hooks/useSweetAlert';
import {
  defaultCabinConfig,
  defaultCabinModeration,
  cabinBannedUsers,
  cabinBannedIPs,
  cabinModHistory,
  cabinaTabs,
  type CabinAdvancedConfig,
  type CabinModerationConfig,
} from '@/mocks/adminSettingsCabina';
import CabinConfigSection from './components/CabinConfigSection';
import CabinModerationSection from './components/CabinModerationSection';
import CabinBansSection from './components/CabinBansSection';

type TabId = typeof cabinaTabs[number]['id'];
type SaveStatus = 'idle' | 'saving' | 'saved';

export default function AdminSettingsCabinaPage() {
  const [activeTab, setActiveTab] = useState<TabId>('config');
  const [cabinConfig, setCabinConfig] = useState<CabinAdvancedConfig>(defaultCabinConfig);
  const [modConfig, setModConfig] = useState<CabinModerationConfig>(defaultCabinModeration);
  const [saveStatus, setSaveStatus] = useState<Record<TabId, SaveStatus>>({
    config: 'idle',
    moderacion: 'idle',
    baneos: 'idle',
  });
  const handleSave = useCallback((tab: TabId) => {
    setSaveStatus((prev) => ({ ...prev, [tab]: 'saving' }));
    setTimeout(() => {
      setSaveStatus((prev) => ({ ...prev, [tab]: 'saved' }));
      SwalAlert.toast('Configuración guardada correctamente', 'success');
      setTimeout(() => {
        setSaveStatus((prev) => ({ ...prev, [tab]: 'idle' }));
      }, 1500);
    }, 600);
  }, []);

  const activeItem = cabinaTabs.find((t) => t.id === activeTab);

  return (
    <div className="min-h-screen bg-background-100">
      {/* Mobile topbar */}
      <header className="lg:hidden sticky top-0 z-30 bg-background-50/98 backdrop-blur-2xl border-b border-background-200">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-red-500 flex items-center justify-center">
              <i className="ri-broadcast-line text-background-50 text-xs"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-sm">Config. Cabina</h1>
              <p className="text-foreground-400 text-[9px]">{activeItem?.label}</p>
            </div>
          </div>
          <Link
            to="/admin/cabina"
            className="flex items-center gap-1.5 text-foreground-500 text-xs hover:text-foreground-700 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Cabina
          </Link>
        </div>
      </header>

      <div className="flex">


        <main className="flex-1 min-w-0">
          {/* Desktop toolbar */}
          <div className="hidden lg:flex items-center justify-between h-16 px-8 border-b border-background-200 bg-background-50 sticky top-0 z-20">
            <div className="flex items-center gap-4">
              <Link
                to="/admin/cabina"
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xs"></i>
                Cabina
              </Link>
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Configuración de Cabina</h2>
                <p className="text-foreground-400 text-xs">
                  Personalizá la experiencia del chat en vivo, moderación y seguridad
                </p>
              </div>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-100 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer"
            >
              <i className="ri-external-link-line text-xs"></i>
              Ver sitio
            </Link>
          </div>

          {/* Content */}
          <div className="p-4 md:p-6 lg:p-8 pb-8">
            <div className="max-w-[900px] mx-auto">
              {/* Stats row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                {[
                  {
                    label: 'Cabina',
                    value: cabinConfig.activarCabina ? 'Activada' : 'Desactivada',
                    icon: 'ri-broadcast-line',
                    color: cabinConfig.activarCabina ? 'text-emerald-500' : 'text-foreground-400',
                    bg: cabinConfig.activarCabina ? 'bg-emerald-100' : 'bg-background-200',
                  },
                  {
                    label: 'Funciones extra',
                    value: `${[cabinConfig.mensajesVoz, cabinConfig.solicitudes, cabinConfig.concursos].filter(Boolean).length} activas`,
                    icon: 'ri-stack-line',
                    color: 'text-violet-500',
                    bg: 'bg-violet-100',
                  },
                  {
                    label: 'Palabras filtradas',
                    value: modConfig.palabrasBloqueadas.length,
                    icon: 'ri-file-text-line',
                    color: 'text-red-500',
                    bg: 'bg-red-100',
                  },
                  {
                    label: 'Baneos activos',
                    value: cabinBannedUsers.filter((b) => b.activo).length,
                    icon: 'ri-forbid-2-line',
                    color: 'text-rose-500',
                    bg: 'bg-rose-100',
                  },
                ].map((s) => (
                  <button
                    key={s.label}
                    onClick={() => {
                      if (s.label === 'Palabras filtradas') setActiveTab('moderacion');
                      else if (s.label === 'Baneos activos') setActiveTab('baneos');
                    }}
                    className="bg-background-50 border border-background-200 rounded-xl p-3.5 text-left cursor-pointer hover:border-secondary-300 transition-all"
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-7 h-7 rounded-lg ${s.bg} flex items-center justify-center`}>
                        <i className={`${s.icon} ${s.color} text-sm`}></i>
                      </div>
                      <span className="text-[10px] text-foreground-400 font-medium">{s.label}</span>
                    </div>
                    <p className="text-sm font-bold text-foreground-900">{s.value}</p>
                  </button>
                ))}
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-1 border-b border-background-200 pb-2 mb-5 overflow-x-auto scrollbar-none">
                {cabinaTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-all ${
                      activeTab === tab.id
                        ? 'bg-red-500 text-background-50'
                        : 'text-foreground-500 hover:bg-background-200 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${tab.icon} text-xs`}></i>
                    {tab.label}
                    {saveStatus[tab.id] === 'saved' && (
                      <i className="ri-check-line text-emerald-400 text-xs animate-pulse ml-0.5"></i>
                    )}
                  </button>
                ))}
              </div>

              {/* Section header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                  <i className={`${activeItem?.icon} text-background-50 text-base`}></i>
                </div>
                <div>
                  <h3 className="text-foreground-950 font-bold text-sm">{activeItem?.label}</h3>
                  <p className="text-foreground-400 text-[10px]">{activeItem?.description}</p>
                </div>
                {saveStatus[activeTab] === 'saving' && (
                  <span className="ml-auto text-foreground-400 text-[10px] flex items-center gap-1">
                    <i className="ri-loader-4-line animate-spin text-xs"></i>
                    Guardando...
                  </span>
                )}
              </div>

              {/* Tab content */}
              <div className="bg-background-50 border border-background-200 rounded-2xl p-5 lg:p-6">
                {activeTab === 'config' && (
                  <CabinConfigSection
                    config={cabinConfig}
                    onChange={setCabinConfig}
                    onSave={() => handleSave('config')}
                    saving={saveStatus.config === 'saving'}
                  />
                )}
                {activeTab === 'moderacion' && (
                  <CabinModerationSection
                    config={modConfig}
                    onChange={setModConfig}
                    onSave={() => handleSave('moderacion')}
                    saving={saveStatus.moderacion === 'saving'}
                  />
                )}
                {activeTab === 'baneos' && (
                  <CabinBansSection
                    users={cabinBannedUsers}
                    ips={cabinBannedIPs}
                    history={cabinModHistory}
                    onSave={() => handleSave('baneos')}
                    saving={saveStatus.baneos === 'saving'}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>




    </div>
  );
}